// Test Out Scoring Configuration and Helpers

// Passing thresholds per phase per exercise type
export const passingThresholds = {
  1: {
    quad: 0.6,
    hamstring: 0.6,
  },
  2: {
    quad: 0.70,
    hamstring: 0.70,
    singleLegHop: 0.80,
    sidePlank: true, // Binary - must pass both sides
  },
  3: {
    quad: 0.80,
    hamstring: 0.80,
    singleLegHop: 0.90,
    rotationalHop: 0.85,
    sidePlank: true,
  },
  4: {
    quad: 0.90,
    hamstring: 0.90,
    singleLegHop: 0.90,
    rotationalHop: 0.90,
    tripleMedialHop: 0.90,
    sidePlank: true,
  },
};

/**
 * Calculate score for a single exercise
 * @param {Object} exercise - Exercise definition
 * @param {Object} data - User's documented data for this exercise
 * @param {string} injurySide - 'left' or 'right'
 * @returns {Object} { score: number|null, passed: boolean, details: object }
 */
export function calculateExerciseScore(exercise, data, injurySide) {
  if (!data) {
    return { score: null, passed: false, details: { error: 'No data provided' } };
  }

  const uninjuredSide = injurySide === 'left' ? 'right' : 'left';

  // Side plank is special - binary yes/no for each side
  if (exercise.isSidePlank) {
    const leftHeld = data.leftHoldTime >= 30;
    const rightHeld = data.rightHoldTime >= 30;
    const passed = leftHeld && rightHeld;
    
    return {
      score: passed ? 1 : 0,
      passed,
      details: {
        leftHoldTime: data.leftHoldTime,
        rightHoldTime: data.rightHoldTime,
        leftPassed: leftHeld,
        rightPassed: rightHeld,
      },
    };
  }

  // For machine exercises with weight - score is injured weight / uninjured weight
  if (exercise.isMachine && exercise.hasWeight) {
    const injuredWeight = injurySide === 'left' ? data.leftWeight : data.rightWeight;
    const uninjuredWeight = injurySide === 'left' ? data.rightWeight : data.leftWeight;
    
    if (!uninjuredWeight || uninjuredWeight === 0) {
      return { score: null, passed: false, details: { error: 'Uninjured side weight is required' } };
    }
    
    const score = injuredWeight / uninjuredWeight;
    
    return {
      score: Math.round(score * 100) / 100,
      passed: false, // Will be determined by phase threshold
      details: {
        injuredWeight,
        uninjuredWeight,
        reps: data.reps,
      },
    };
  }

  // For hop tests - distance based
  if (exercise.hasDistance) {
    const injuredDistance = injurySide === 'left' ? data.leftDistance : data.rightDistance;
    const uninjuredDistance = injurySide === 'left' ? data.rightDistance : data.leftDistance;
    
    if (!uninjuredDistance || uninjuredDistance === 0) {
      return { score: null, passed: false, details: { error: 'Uninjured side distance is required' } };
    }
    
    const score = injuredDistance / uninjuredDistance;
    
    return {
      score: Math.round(score * 100) / 100,
      passed: false,
      details: {
        injuredDistance,
        uninjuredDistance,
      },
    };
  }

  // For exercises with reps (non-machine strength tests)
  if (exercise.hasReps) {
    const injuredReps = injurySide === 'left' ? data.leftReps : data.rightReps;
    const uninjuredReps = injurySide === 'left' ? data.rightReps : data.leftReps;
    
    if (!uninjuredReps || uninjuredReps === 0) {
      return { score: null, passed: false, details: { error: 'Uninjured side reps is required' } };
    }
    
    const score = injuredReps / uninjuredReps;
    
    return {
      score: Math.round(score * 100) / 100,
      passed: false,
      details: {
        injuredReps,
        uninjuredReps,
      },
    };
  }

  return { score: null, passed: false, details: { error: 'Unknown exercise type' } };
}

/**
 * Determine if an exercise passed based on phase threshold
 * @param {number} phase - Phase number (1-4)
 * @param {string} scoringKey - Exercise scoring key (quad, hamstring, etc.)
 * @param {number} score - Calculated score (0-1+)
 * @returns {boolean}
 */
export function didExercisePass(phase, scoringKey, score) {
  const threshold = passingThresholds[phase]?.[scoringKey];
  
  if (threshold === undefined) {
    console.warn(`No threshold found for phase ${phase}, key ${scoringKey}`);
    return false;
  }
  
  // Side plank is boolean
  if (threshold === true) {
    return score === 1;
  }
  
  return score >= threshold;
}

/**
 * Calculate all exercise scores and determine overall pass/fail
 * @param {number} phase - Phase number
 * @param {Array} exercises - Array of exercise definitions
 * @param {Object} exerciseData - Object keyed by exercise ID with user data
 * @param {string} injurySide - 'left' or 'right'
 * @returns {Object} { overallPassed, exerciseResults, summary }
 */
export function calculateTestOutResults(phase, exercises, exerciseData, injurySide) {
  const exerciseResults = {};
  let allPassed = true;
  
  for (const exercise of exercises) {
    const data = exerciseData[exercise.id];
    const scoreResult = calculateExerciseScore(exercise, data, injurySide);
    
    // Determine if passed based on threshold
    const passed = scoreResult.score !== null 
      ? didExercisePass(phase, exercise.scoringKey, scoreResult.score)
      : false;
    
    exerciseResults[exercise.id] = {
      exerciseId: exercise.id,
      displayName: exercise.displayName,
      scoringKey: exercise.scoringKey,
      score: scoreResult.score,
      passed,
      threshold: passingThresholds[phase]?.[exercise.scoringKey],
      details: scoreResult.details,
    };
    
    if (!passed) {
      allPassed = false;
    }
  }
  
  // Build summary
  const summary = {
    phase,
    totalExercises: exercises.length,
    passedExercises: Object.values(exerciseResults).filter(r => r.passed).length,
    failedExercises: Object.values(exerciseResults).filter(r => !r.passed).length,
  };
  
  return {
    overallPassed: allPassed,
    exerciseResults,
    summary,
  };
}

/**
 * Format score as percentage string
 * @param {number} score 
 * @returns {string}
 */
export function formatScoreAsPercent(score) {
  if (score === null || score === undefined) return '--';
  return `${Math.round(score * 100)}%`;
}

/**
 * Get threshold as percentage string
 * @param {number} threshold 
 * @returns {string}
 */
export function formatThresholdAsPercent(threshold) {
  if (threshold === true) return 'Yes';
  if (threshold === null || threshold === undefined) return '--';
  return `≥${Math.round(threshold * 100)}%`;
}